import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useGetDeployedAgentsQuery,
  useGetPlatformAdminOrganisationsQuery,
  useTestPlaceAiCallMutation,
  useTestTriggerWorkflowMutation,
} from "@/store/admin_api";
import React from "react";

export default function AiTestTriggers() {
  const { data: organisations } = useGetPlatformAdminOrganisationsQuery();
  const [placeTestCall, { isLoading: isPlacingCall }] = useTestPlaceAiCallMutation();
  const [triggerWorkflow, { isLoading: isTriggeringWorkflow }] = useTestTriggerWorkflowMutation();

  const [searchParams] = useSearchParams();
  const { data: deployedAgentPage } = useGetDeployedAgentsQuery({ page: 0, size: 200 });

  const [directOrgId, setDirectOrgId] = useState(searchParams.get("organisation") ?? "");
  const [directAgentId, setDirectAgentId] = useState(searchParams.get("agent") ?? "");
  const [directToNumber, setDirectToNumber] = useState("");
  const [directFromPhoneNumberId, setDirectFromPhoneNumberId] = useState("");
  const [directContactId, setDirectContactId] = useState("");
  const [directCallScope, setDirectCallScope] = useState("");

  const [workflowId, setWorkflowId] = useState("");
  const [workflowContactId, setWorkflowContactId] = useState("");
  const [workflowDealId, setWorkflowDealId] = useState("");

  const organisationAgents = (deployedAgentPage?.items ?? []).filter(
    (agent) => agent.organisation?.id === directOrgId,
  );

  const handleOrganisationChange = (organisationId: string) => {
    setDirectOrgId(organisationId);
    setDirectAgentId("");
  };

  const handlePlaceTestCall = async () => {
    if (!directOrgId || !directAgentId || !directToNumber) {
      toast.error("Organisation, agent, and destination number are required");
      return;
    }
    try {
      const placedCall = await placeTestCall({
        organisationId: directOrgId,
        agentId: directAgentId,
        toPhoneNumber: directToNumber,
        fromPhoneNumberId: directFromPhoneNumberId || null,
        contactId: directContactId || null,
        callScope: directCallScope || null,
      }).unwrap();
      toast.success(`Call placed — id ${placedCall.id}, sid ${placedCall.carrierCallSid ?? "pending"}`);
    } catch (placeError: any) {
      toast.error(placeError?.data?.detail || "Failed to place call");
    }
  };

  const handleTriggerWorkflow = async () => {
    if (!workflowId) {
      toast.error("Workflow ID is required");
      return;
    }
    try {
      const execution = await triggerWorkflow({
        workflowId,
        contactId: workflowContactId || undefined,
        dealId: workflowDealId || undefined,
      }).unwrap();
      toast.success(
        `Workflow triggered — execution ${execution.id} with ${execution.steps?.length ?? 0} step(s)`,
      );
    } catch (triggerError: any) {
      toast.error(triggerError?.data?.detail || "Failed to trigger workflow");
    }
  };

  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">AI Test Triggers</h1>
        <p className="text-muted-foreground">
          Smoke-test the voice stack and workflow-triggered outbound calls end-to-end.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Place Test AI Call</CardTitle>
          <CardDescription>
            Direct outbound AI call. Bypasses workflows so you can iterate on an agent's prompt and voice without
            editing a workflow each time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Organisation *</Label>
              <Select value={directOrgId} onValueChange={handleOrganisationChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick an organisation" />
                </SelectTrigger>
                <SelectContent>
                  {organisations?.map((organisation) => (
                    <SelectItem key={organisation.id} value={organisation.id!}>
                      {organisation.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>AI Agent *</Label>
              <Select value={directAgentId} onValueChange={setDirectAgentId} disabled={!directOrgId}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !directOrgId
                        ? "Pick an organisation first"
                        : organisationAgents.length === 0
                          ? "This organisation has no agents"
                          : "Pick an agent"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {organisationAgents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Destination number (E.164) *</Label>
              <Input
                type="tel"
                value={directToNumber}
                onChange={(changeEvent) => setDirectToNumber(changeEvent.target.value)}
                placeholder="+15551234567"
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                From phone number ID <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                value={directFromPhoneNumberId}
                onChange={(changeEvent) => setDirectFromPhoneNumberId(changeEvent.target.value)}
                placeholder="Defaults to first active phone on the org"
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                Contact ID <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                value={directContactId}
                onChange={(changeEvent) => setDirectContactId(changeEvent.target.value)}
                placeholder="Links the call to an existing contact"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>
              Call scope <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              value={directCallScope}
              onChange={(changeEvent) => setDirectCallScope(changeEvent.target.value)}
              placeholder="Additional context appended to the agent's system prompt for this call only"
              rows={3}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handlePlaceTestCall} disabled={isPlacingCall}>
              {isPlacingCall && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Place Test Call
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Trigger Workflow</CardTitle>
          <CardDescription>
            Manually create a workflow execution for a given workflow against a contact/deal. Use this to exercise
            PLACE_AI_CALL and other async steps end-to-end.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Workflow ID *</Label>
              <Input
                value={workflowId}
                onChange={(changeEvent) => setWorkflowId(changeEvent.target.value)}
                placeholder="UUID of a workflow"
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                Contact ID <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                value={workflowContactId}
                onChange={(changeEvent) => setWorkflowContactId(changeEvent.target.value)}
                placeholder="Required if any step needs a contact"
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                Deal ID <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                value={workflowDealId}
                onChange={(changeEvent) => setWorkflowDealId(changeEvent.target.value)}
                placeholder="For deal-scoped workflows"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleTriggerWorkflow} disabled={isTriggeringWorkflow}>
              {isTriggeringWorkflow && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Trigger Workflow
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
